import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import {
  ALLOWED_EVIDENCE_PHOTO_EXTENSIONS,
  ALLOWED_EVIDENCE_PHOTO_TYPES,
  EvidencePhotoValue,
  useEvidencePhotoUpload,
} from "./useEvidencePhotoUpload";

// Derived from the hook's own allowlists so the file picker's accept filter can never drift out
// of sync with what useEvidencePhotoUpload actually validates. Extensions are included alongside
// MIME types (not just the MIME types alone) because the OS/browser file picker filters on
// `accept` before any of this component's own validation runs -- an existing HEIC photo from an
// officer's library can have an empty MIME type on many iOS versions (see
// ALLOWED_EVIDENCE_PHOTO_EXTENSIONS's own comment), and a MIME-only accept value can filter that
// file out of the picker entirely before useEvidencePhotoUpload's extension-based fallback ever
// gets a chance to accept it.
export const EVIDENCE_PHOTO_ACCEPT = [...ALLOWED_EVIDENCE_PHOTO_TYPES, ...ALLOWED_EVIDENCE_PHOTO_EXTENSIONS].join(",");

// "JPG" here (not "JPEG") is the file-extension convention shown to the officer, matching the
// mockup; ALLOWED_EVIDENCE_PHOTO_TYPES still validates against the image/jpeg MIME type either way.
const SUPPORTED_FORMATS_LABEL = "JPG, PNG, or HEIC";

// Total height of the preview box, fixed rather than a minimum -- see that Box's sx for why.
const PREVIEW_BOX_HEIGHT = 200;
// Matches the preview box's own `p: 2` (MUI spacing(2) = 16px) on each side. The preview
// image's own max-height is capped to the space actually left over after that padding
// (rather than a plain 100%, which wouldn't resolve reliably through the unheighted Stack
// between the box and the image) so a tall image can never render past the box's edge into
// where Paper's overflow: hidden would silently crop it.
const PREVIEW_BOX_VERTICAL_PADDING = 16;
const PREVIEW_IMAGE_MAX_HEIGHT = PREVIEW_BOX_HEIGHT - PREVIEW_BOX_VERTICAL_PADDING * 2;

export interface EvidencePhotoUploadProps {
  label: string;
  value: EvidencePhotoValue | null;
  onChange: (value: EvidencePhotoValue | null) => void;
  required?: boolean;
  /**
   * Formik's error for this field (only meaningful once the field is touched -- the caller,
   * not this component, decides that, since only the caller knows whether a submit attempt has
   * actually happened for a field with no native blur event of its own to touch it). Shown only
   * when there's no more specific file-validation error already displayed below, since both
   * conditions mean the same underlying thing -- no valid photo attached -- and showing both at
   * once would just be redundant.
   */
  requiredError?: string;
}

export const EvidencePhotoUpload: React.FC<EvidencePhotoUploadProps> = ({
  label,
  value,
  onChange,
  required,
  requiredError,
}) => {
  const captureInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const { handleFileSelected, handleRemove, error } = useEvidencePhotoUpload(onChange);
  // HEIC/HEIF (the iPhone default this widget explicitly accepts) isn't decodable as an <img>
  // src in most non-Safari browsers -- rather than guessing by MIME type, which HEIC-capable
  // browser support could shift under, react to the <img> actually failing to render and fall
  // back to a generic file chip. Reset whenever a new file comes in, since the failure applies
  // to the previous preview URL, not necessarily the new one.
  const [previewFailed, setPreviewFailed] = useState(false);
  useEffect(() => {
    setPreviewFailed(false);
  }, [value?.previewUrl]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelected(file);
    }
    event.target.value = "";
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {label}
        {required && (
          <Box component="span" sx={{ color: "error.main" }}>
            {" "}
            *
          </Box>
        )}
      </Typography>
      {/* Mirrors Scorecard's card shape (components/src/PayerOverview/PayerOverview.tsx):
          Paper with the same borderRadius, plus overflow: hidden so the header bar's square
          corners get clipped to the Paper's rounded ones rather than poking out past them. */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ bgcolor: "grey.900", color: "common.white", px: 2, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {label}
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor: "grey.100",
            // Fixed, not minHeight -- a preview image sizes itself to its own aspect ratio, so
            // License Plate Capture and Vehicle in Violation (landscape vs. portrait source
            // photos are equally likely) would otherwise render as different-height cards
            // side by side. See PREVIEW_IMAGE_MAX_HEIGHT for how the image itself is kept
            // from growing past this box instead of stretching it.
            height: PREVIEW_BOX_HEIGHT,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            p: 2,
            // Anchors the remove button below -- see that button's own comment for why.
            position: "relative",
          }}
        >
          {value ? (
            <>
              {previewFailed ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <InsertDriveFileIcon color="action" />
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {value.file.name}
                  </Typography>
                </Stack>
              ) : (
                <img
                  src={value.previewUrl}
                  alt={label}
                  onError={() => setPreviewFailed(true)}
                  style={{
                    maxWidth: "100%",
                    maxHeight: PREVIEW_IMAGE_MAX_HEIGHT,
                    width: "auto",
                    height: "auto",
                    borderRadius: 4,
                  }}
                />
              )}
              {/* Fixed to this box's own corner rather than sharing a row with the preview
                  content -- a low-aspect-ratio source photo is capped only by maxHeight above, so
                  its scaled-up width can exceed the card's real width, and sharing a row with it
                  pushed this button out past Paper's overflow: hidden edge along with it.
                  Anchoring to the box itself means the button's position never depends on how
                  wide the preview content ends up rendering. */}
              <IconButton
                aria-label={`Remove ${label}`}
                onClick={handleRemove}
                size="small"
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  bgcolor: "background.paper",
                  boxShadow: 1,
                  "&:hover": { bgcolor: "grey.200" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => captureInputRef.current?.click()}
                >
                  Capture
                </Button>
                <Button
                  variant="outlined"
                  sx={{ bgcolor: "common.white" }}
                  startIcon={<UploadFileIcon />}
                  onClick={() => uploadInputRef.current?.click()}
                >
                  Upload
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {SUPPORTED_FORMATS_LABEL}
              </Typography>
            </>
          )}
        </Box>
      </Paper>
      <input
        ref={captureInputRef}
        type="file"
        accept={EVIDENCE_PHOTO_ACCEPT}
        capture="environment"
        hidden
        onChange={handleInputChange}
      />
      <input
        ref={uploadInputRef}
        data-testid="evidence-photo-upload-input"
        type="file"
        accept={EVIDENCE_PHOTO_ACCEPT}
        hidden
        onChange={handleInputChange}
      />
      {(error || requiredError) && (
        <Typography variant="caption" color="error" display="block">
          {error || requiredError}
        </Typography>
      )}
    </Box>
  );
};

export default EvidencePhotoUpload;
